import { WallPropConfig } from "@/state/RoomConfig/WallConfig";
import React, { useMemo } from "react";
import { BufferGeometry, Color, Float32BufferAttribute, Vector3Like } from "three";

type Props = {
    width: number;
    height: number;
    length: number;
    color: Color;
    wallProps: WallPropConfig[];
};

const DEBUG_BASEBOARD = false;

export const Baseboard: React.FC<Props> = ({ width, height, length, color, wallProps }) => {
    const geometry = useMemo(() => {
        const sections = getBaseboardSections(wallProps, length, height);
        return generateBaseboardGeometry(width, height, length, sections);
    }, [width, height, length, wallProps]);

    return (
        <group>
            <mesh receiveShadow>
                <bufferGeometry attach="geometry" {...geometry} />
                <meshPhysicalMaterial roughness={0.5} metalness={0} color={color} />
            </mesh>
            {DEBUG_BASEBOARD && (
                <mesh>
                    <bufferGeometry attach="geometry" {...geometry} />
                    <meshBasicMaterial color={"red"} wireframe />
                </mesh>
            )}
        </group>
    );
};

const BASEBOARD_PROFILE = [
    { y: 0, z: 0.15 },
    { y: 0.75, z: 0.1 },
    { y: 0.775, z: 0.05 },
    { y: 0.82, z: 0.08 },
    { y: 0.96, z: 0.06 },
    { y: 1, z: 0 },
];

const getBaseboardSections = (wallProps: WallPropConfig[], length: number, height: number) => {
    const start = 0;
    const end = length;

    if (wallProps.length === 0) {
        return [start, end];
    }

    const sections = [start];

    for (const prop of wallProps) {
        if ((prop.type === "window" && prop.floorOffset < height) || prop.type === "door") {
            sections.push(prop.offset, prop.offset + prop.width);
        }
    }

    sections.sort((a, b) => a - b);
    sections.push(end);

    return sections;
};

const generateBaseboardGeometry = (
    width: number,
    height: number,
    length: number,
    sections: number[]
) => {
    const vertices = Array<number>();

    const triangulateFace = (points: Vector3Like[]) => {
        if (points.length < 3) {
            return;
        }

        const p0 = points[0];
        for (let i = 1; i < points.length - 1; i++) {
            const p1 = points[i];
            const p2 = points[i + 1];

            vertices.push(p0.x, p0.y * height, p0.z * width);
            vertices.push(p1.x, p1.y * height, p1.z * width);
            vertices.push(p2.x, p2.y * height, p2.z * width);
        }
    };

    let pointBuffer = Array<Vector3Like>();
    const generateSideFace = (
        x: number,
        p0: { y: number; z: number },
        p1: { y: number; z: number },
        mirror?: boolean
    ) => {
        pointBuffer = [];

        if (p0.z !== 0) {
            pointBuffer.push({ x, y: p0.y, z: 0 });
        }

        pointBuffer.push({ x, y: p0.y, z: p0.z });
        pointBuffer.push({ x, y: p1.y, z: p1.z });

        if (p1.z !== 0) {
            pointBuffer.push({ x, y: p1.y, z: 0 });
        }

        if (mirror) {
            pointBuffer.reverse();
        }

        triangulateFace(pointBuffer);
    };

    const generateFrontFace = (
        x0: number,
        x1: number,
        p0: { y: number; z: number },
        p1: { y: number; z: number }
    ) => {
        pointBuffer = [];
        pointBuffer.push({ x: x0, y: p0.y, z: p0.z });
        pointBuffer.push({ x: x1, y: p0.y, z: p0.z });
        pointBuffer.push({ x: x1, y: p1.y, z: p1.z });
        pointBuffer.push({ x: x0, y: p1.y, z: p1.z });

        triangulateFace(pointBuffer);
    };

    // generate right side
    for (let i = 0; i < sections.length - 1; i += 2) {
        const start = sections[i];
        const end = sections[i + 1];
        for (let j = 0; j < BASEBOARD_PROFILE.length - 1; j++) {
            const p0 = BASEBOARD_PROFILE[j];
            const p1 = BASEBOARD_PROFILE[j + 1];

            generateSideFace(start, p0, p1);

            generateFrontFace(start, end, p0, p1);

            generateSideFace(end, p0, p1, true);
        }
    }

    const bufferGeometry = new BufferGeometry();
    bufferGeometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));

    bufferGeometry.computeVertexNormals();

    return bufferGeometry;
};
